var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { unwrapCortexResponse } from 'libs.consent.react';
import { Spinner, Text, useLocalizeMessage } from 'libs.nucleus';
import { ApiClientService, AuthContext, AuthRoute, useApiClient } from 'libs.react_internal';
import { resolveConsentDocumentContext } from './consent_document.helpers';
import { buildParticipantConsentPath, extractPrimaryParticipantId, findEnvironmentIdByOrgCode, } from '../../helpers/consent_participant_path';
/**
 * Landing route for the document link in a consent-completion email
 * (`/consent-document?doc=&org=&env=`), built by econsent-enviroment's
 * `buildDocumentLink`. Internal signers used to be sent to the legacy site app
 * because Cortex cannot build a participant-scoped Lumos path — it does not hold
 * the MOM environment id. This route resolves that id from the org code and the
 * participant from the document id, then forwards to the participant's Consent
 * tab, so the link only has to carry what Cortex actually knows.
 */
export const ConsentDocumentPage = () => {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const translate = useLocalizeMessage();
    const { entityId, isLoading: isAuthLoading, isLoggedIn, user } = useContext(AuthContext);
    const momClient = useApiClient(ApiClientService.MOM);
    const identityId = user === null || user === void 0 ? void 0 : user.id;
    const [error, setError] = useState('');
    const documentContext = useMemo(() => resolveConsentDocumentContext({
        documentId: searchParams.get('doc') || '',
        orgCode: searchParams.get('org') || '',
        environmentKey: searchParams.get('env') || '',
    }), [searchParams]);
    const loginPath = useMemo(() => {
        const redirect = `${window.location.origin}${location.pathname}${location.search}`;
        const params = new URLSearchParams({ redirect });
        return `${AuthRoute.LOGIN}?${params.toString()}`;
    }, [location.pathname, location.search]);
    // This route is whitelisted so the deep link survives an unauthenticated hit:
    // the router's own `navigateToLogin()` is a bare `navigate(AuthRoute.LOGIN)`
    // and would discard the document the signer was trying to open. Same reason
    // and same shape as the consent-invite route.
    useEffect(() => {
        if (!documentContext || isAuthLoading || isLoggedIn) {
            return;
        }
        navigate(loginPath, { replace: true });
    }, [documentContext, isAuthLoading, isLoggedIn, loginPath, navigate]);
    useEffect(() => {
        if (!documentContext || isAuthLoading || !isLoggedIn || !entityId || !identityId) {
            return;
        }
        let isActive = true;
        const resolveDocumentPath = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            try {
                // Maps the authenticated IAM identity to its Cortex account, which the
                // account-scoped document route below is keyed on. Same call the invite
                // flow uses for the same purpose.
                const accountResponse = yield documentContext.accountApi.get('/v2/hub/accounts/login');
                const account = unwrapCortexResponse(accountResponse);
                if (!(account === null || account === void 0 ? void 0 : account._id)) {
                    throw new Error('The account response was incomplete.');
                }
                const [signedDocResponse, environmentsResponse] = yield Promise.all([
                    documentContext.accountApi.get(`/v2/account/${account._id}/ec__site_documents/${documentContext.documentId}`, { params: { paths: ['ec__primary_participant'], passive: true } }),
                    momClient.get(`/v1/entities/${entityId}/identities/${identityId}/environmentSites?page=1&limit=100`),
                ]);
                const participantId = extractPrimaryParticipantId(unwrapCortexResponse(signedDocResponse));
                const environmentId = findEnvironmentIdByOrgCode((_a = environmentsResponse === null || environmentsResponse === void 0 ? void 0 : environmentsResponse.data) === null || _a === void 0 ? void 0 : _a.data, documentContext.orgCode);
                if (!isActive) {
                    return;
                }
                if (!participantId || typeof environmentId !== 'number') {
                    throw new Error('The document could not be matched to a participant in this workspace.');
                }
                navigate(buildParticipantConsentPath(environmentId, participantId), { replace: true });
            }
            catch (resolveError) {
                console.error('Consent document link could not be resolved', resolveError);
                // Surfaced rather than bounced to '/': arriving from an email, a silent
                // redirect to the app default is indistinguishable from a broken link.
                if (isActive) {
                    setError(translate('This consent document could not be opened. It may no longer be available to you.'));
                }
            }
        });
        void resolveDocumentPath();
        return () => {
            isActive = false;
        };
    }, [documentContext, entityId, identityId, isAuthLoading, isLoggedIn, momClient, navigate, translate]);
    if (!documentContext) {
        return (_jsx("div", { role: 'alert', "data-testid": 'consent-document-invalid', children: _jsx(Text, { children: translate('This consent document link is invalid or incomplete.') }) }));
    }
    if (error) {
        return (_jsx("div", { role: 'alert', "data-testid": 'consent-document-error', children: _jsx(Text, { children: error }) }));
    }
    return _jsx(Spinner, { wrapper: 'full' });
};
